
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Edit, Trash2 } from 'lucide-react';
import { FarmPrice } from '@/types/database/farm-prices';
import { useFinancePagination } from '@/hooks/use-finance-pagination';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

interface FarmPricesTableProps {
  prices: FarmPrice[];
  onEdit: (price: FarmPrice) => void;
  onDelete: (id: string) => void;
}

const FarmPricesTable: React.FC<FarmPricesTableProps> = ({ prices, onEdit, onDelete }) => {
  const {
    paginatedData,
    currentPage,
    totalPages,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    hasNextPage,
    hasPreviousPage,
    startIndex,
    endIndex,
    totalItems,
  } = useFinancePagination({ data: prices, itemsPerPage: 10 });

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink
            onClick={() => goToPage(i)}
            isActive={currentPage === i}
            className="cursor-pointer"
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    return items;
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product Name</TableHead>
              <TableHead>Region</TableHead>
              <TableHead>Origin</TableHead>
              <TableHead>Source Type</TableHead>
              <TableHead>Supplier Name</TableHead>
              <TableHead className="text-right">Farm Price/Kg</TableHead>
              <TableHead className="text-right">Total Price/Kg</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedData.map((price) => (
              <TableRow key={price.id}>
                <TableCell className="font-medium">{price.product_name}</TableCell>
                <TableCell>{price.region}</TableCell>
                <TableCell>{price.origin || '-'}</TableCell>
                <TableCell>{price.source_type || '-'}</TableCell>
                <TableCell>{price.supplier_name || '-'}</TableCell>
                <TableCell className="text-right">
                  ETB {price.farm_price_per_kg.toFixed(2)}
                </TableCell>
                <TableCell className="text-right font-medium">
                  ETB {price.price.toFixed(2)}
                </TableCell>
                <TableCell>{new Date(price.date).toLocaleDateString()}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(price)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(price.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {paginatedData.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No farm prices found. Add some prices to get started.
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {startIndex} to {endIndex} of {totalItems} entries
          </div>
          
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={hasPreviousPage ? goToPreviousPage : undefined}
                  className={hasPreviousPage ? "cursor-pointer" : "cursor-not-allowed opacity-50"}
                />
              </PaginationItem>
              
              {renderPaginationItems()}
              
              <PaginationItem>
                <PaginationNext
                  onClick={hasNextPage ? goToNextPage : undefined}
                  className={hasNextPage ? "cursor-pointer" : "cursor-not-allowed opacity-50"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default FarmPricesTable;
